import React, { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Button, Snackbar, Alert } from "@mui/material";
import { Upload } from "@mui/icons-material";
import useUploadPastOrders from "../../../queries/routes/useUploadPastOrders";

interface OrderImportProps {
  organizationId: string;
  buttonText?: string;
  buttonVariant?: "text" | "outlined" | "contained";
  buttonSize?: "small" | "medium" | "large";
  showSnackbar?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export interface OrderImportRef {
  triggerUpload: () => void;
}

const OrderImport = forwardRef<OrderImportRef, OrderImportProps>(({
  organizationId,
  buttonText = "Upload Past Orders",
  buttonVariant = "outlined",
  buttonSize = "small",
  showSnackbar = true,
  onSuccess,
  onError,
}, ref) => {
  const uploadPastOrders = useUploadPastOrders();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
    errors?: string[];
  }>({ open: false, message: "", severity: "success" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadPastOrders = () => {
    fileInputRef.current?.click();
  };

  useImperativeHandle(ref, () => ({
    triggerUpload: handleUploadPastOrders,
  }));

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && organizationId) {
      uploadPastOrders.mutate(
        { file, organizationId },
        {
          onSuccess: (data) => {
            console.log("Upload success data:", data);
            let message = `Successfully processed ${data.processed_orders} orders.`;

            if (data.existing_orders > 0) {
              message += ` ${data.existing_orders} orders were previously imported and skipped.`;
            }

            // if (data.errors && data.errors.length > 0) {
            //   message += ` Errors: ${data.errors.length}`;
            // }

            console.log("Setting snackbar with message:", message, "errors:", data.errors);

            if (showSnackbar) {
              setSnackbar({
                open: true,
                message,
                severity: "success",
              });
              // setSnackbar({
              //   open: true,
              //   message,
              //   severity: (data.errors && data.errors.length > 0) ? "error" : "success",
              //   errors: (data.errors && data.errors.length > 0) ? data.errors : undefined,
              // });
            }

            if (onSuccess) {
              onSuccess(data);
            }
          },
          onError: (error) => {
            console.log("Upload error:", error);
            const message = error.message || "Failed to upload past orders";

            console.log("Setting error snackbar with message:", message);

            if (showSnackbar) {
              setSnackbar({
                open: true,
                message,
                severity: "error",
              });
            }

            if (onError) {
              onError(error);
            }
          },
        }
      );
    }
    // Reset the input
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      <Button
        variant={buttonVariant}
        size={buttonSize}
        startIcon={<Upload />}
        onClick={handleUploadPastOrders}
        disabled={uploadPastOrders.isLoading}
        sx={{
          textTransform: "none",
          fontWeight: 500,
        }}
      >
        {uploadPastOrders.isLoading ? "Uploading..." : buttonText}
      </Button>
      {showSnackbar && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.errors ? 15000 : 6000} // Longer duration for errors so users can read them
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%", maxWidth: "500px" }}
          >
            <div>
              <div>{snackbar.message}</div>
              {snackbar.errors && snackbar.errors.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>Error Details:</div>
                  <div style={{ maxHeight: "200px", overflowY: "auto", fontSize: "0.875rem" }}>
                    {snackbar.errors.map((error, index) => (
                      <div key={index} style={{ marginBottom: "2px", paddingLeft: "8px" }}>
                        • {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Alert>
        </Snackbar>
      )}
    </>
  );
});

export default OrderImport;
